import React, { useMemo, useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { AvailabilitiesList as View } from '@views';
import { useSelector, useDispatch } from 'react-redux';
import {
  applyAvailabilityToCategory,
  applyAvailabilityToItem,
  deleteAvailability,
  editAvailability,
  createAvailabilityForItem,
  createAvailabilityForCategory,
  applyAvailabilityToSpecialCategory,
  applyAvailabilityToSpecialSize,
  createAvailabilityForSpecialCategory,
  createAvailabilityForSpecialSize,
} from '@store/menu/duck';
import { getAllAvailabilitiesExceptApplied } from '@helpers/itemEdit';
import { LOADING, FAILURE, SUCCESS } from '@constants/requestPhase';

const AvailabilitiesList = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();

  const {
    availabilities,
    applyAvailabilityToCategoryPhase,
    applyAvailabilityToItemPhase,
    deleteAvailabilityPhase,
    editAvailabilityPhase,
    createAvailabilityForItemPhase,
    createAvailabilityForCategoryPhase,
    createAvailabilityForSpecialCategoryPhase,
    createAvailabilityForSpecialSizePhase,
    applyAvailabilityToSpecialCategoryPhase,
    applyAvailabilityToSpecialSizePhase,
  } = useSelector(state => state.menu);

  const id = location.state?.id;
  const appliedIds = location.state?.appliedIds;
  const entity = location.state?.entity;

  const memoizedAvailabilities = useMemo(() => (
    getAllAvailabilitiesExceptApplied(availabilities, appliedIds)
  ), [entity, availabilities]);

  const [error, setError] = useState(null);
  const [availabilityList, setAvailabilityList] = useState(memoizedAvailabilities);
  const [isAvailabilityModalOpen, setIsAvailabilityModalOpen] = useState(false);

  const [availabilityId, setAvailabilityId] = useState(null);
  const [availabilityName, setAvailabilityName] = useState('');
  const [selectedWeekDays, setSelectedWeekDays] = useState([]);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isAvailabilityDeleteModalOpen, setIsAvailabilityDeleteModalOpen] = useState(false);

  // Check if something has changed (we don't need to edit availability if info is the same)
  const isDataTheSame = (!!availabilityId && !!availabilities[availabilityId]
    && startDate
    && ((!endDate && !availabilities[availabilityId].endDate) || endDate)
    && startTime
    && endTime
    && (availabilities[availabilityId].name === availabilityName)
    && (availabilities[availabilityId].startDate === startDate.toISOString())
    && availabilities[availabilityId].startTime === startTime.toISOString()
    && ((availabilities[availabilityId].endDate && availabilities[availabilityId].endDate === endDate.toISOString())
    || (!availabilities[availabilityId].endDate && !endDate))
    && availabilities[availabilityId].endTime === endTime.toISOString()
    && JSON.stringify(selectedWeekDays) === JSON.stringify(availabilities[availabilityId].weekDays));

  const isConfirmButtonDisabled = !availabilityName
  || !selectedWeekDays.length
  || !startTime
  || !endTime
  || !startDate
  || new Date(startTime.setDate(1)).toISOString() > new Date(endTime.setDate(1)).toISOString()
  // set date to have the same date and compare only time
  || (!!endDate && new Date(startDate).toISOString() > new Date(endDate).toISOString());

  useEffect(() => {
    if (!location.state || !location.state.id) {
      history.goBack();
    }
  }, []);

  const handleToastClose = () => {
    setError(null);
  };

  const handleDayClick = dayId => () => {
    const days = [...selectedWeekDays];
    if (days.includes(dayId)) {
      const index = days.indexOf(dayId);

      if (index > -1) {
        days.splice(index, 1);
      }
    } else {
      days.push(dayId);
    }
    setSelectedWeekDays(days);
  };

  const handleAvailabilityDeleteModalChangeState = () => {
    setIsAvailabilityDeleteModalOpen(prevState => !prevState);
  };

  const handleAvailabilityNameChange = e => {
    setAvailabilityName(e.target.value);
  };

  const clearInputs = () => {
    setAvailabilityId(null);
    setAvailabilityName('');
    setStartTime('');
    setEndTime('');
    setStartDate('');
    setEndDate('');
    setSelectedWeekDays([]);
  };

  useEffect(() => {
    if (applyAvailabilityToCategoryPhase === SUCCESS
      || applyAvailabilityToItemPhase === SUCCESS
      || applyAvailabilityToSpecialCategoryPhase === SUCCESS
      || applyAvailabilityToSpecialSizePhase === SUCCESS) {
      history.goBack();
    }
  }, [applyAvailabilityToCategoryPhase, applyAvailabilityToItemPhase,
    applyAvailabilityToSpecialCategoryPhase, applyAvailabilityToSpecialSizePhase]);

  useEffect(() => {
    if (createAvailabilityForItemPhase === SUCCESS
      || createAvailabilityForCategoryPhase === SUCCESS
      || createAvailabilityForSpecialSizePhase === SUCCESS
      || createAvailabilityForSpecialCategoryPhase === SUCCESS) {
      history.goBack();
    }
    if (createAvailabilityForItemPhase === FAILURE
      || createAvailabilityForCategoryPhase === FAILURE
      || createAvailabilityForSpecialCategoryPhase === FAILURE
      || createAvailabilityForSpecialSizePhase === FAILURE) {
      setIsAvailabilityModalOpen(false);
    }
  }, [createAvailabilityForItemPhase, createAvailabilityForCategoryPhase,
    createAvailabilityForSpecialCategoryPhase, createAvailabilityForSpecialSizePhase]);

  useEffect(() => {
    if (editAvailabilityPhase === SUCCESS
      || deleteAvailabilityPhase === SUCCESS) {
      setAvailabilityList(getAllAvailabilitiesExceptApplied(availabilities, appliedIds));
      clearInputs();
      setIsAvailabilityDeleteModalOpen(false);
      setIsAvailabilityModalOpen(false);
    }
    if (editAvailabilityPhase === FAILURE
      || deleteAvailabilityPhase === FAILURE) {
      setIsAvailabilityModalOpen(false);
    }
  }, [deleteAvailabilityPhase, editAvailabilityPhase]);

  const onBackUpClick = () => {
    history.goBack();
  };

  const handleAvailabilityModalChangeState = () => {
    clearInputs();
    setIsAvailabilityModalOpen(prevState => !prevState);
  };

  const handleAvailabilityAdd = () => {
    setIsAvailabilityModalOpen(true);
  };

  const handleStartTimeChange = time => {
    setStartTime(time);
  };

  const handleEndTimeChange = time => {
    setEndTime(time);
  };

  const handleStartDateChange = date => {
    setStartDate(date);
  };

  const handleEndDateChange = date => {
    setEndDate(date);
  };

  const handleAvailabilityEdit = (event, editingId) => {
    event.stopPropagation(); // To not call parent's handler

    setIsAvailabilityModalOpen(true);
    setAvailabilityId(editingId);
    setAvailabilityName(availabilities[editingId].name);
    setStartTime(new Date(availabilities[editingId].startTime));
    setEndTime(new Date(availabilities[editingId].endTime));
    setStartDate(new Date(availabilities[editingId].startDate));
    setEndDate(availabilities[editingId].endDate ? new Date(availabilities[editingId].endDate) : '');
    setSelectedWeekDays(availabilities[editingId].weekDays);
  };

  const handleAvailabilityApply = availabilityApplyId => () => {
    switch (entity) {
      case 'category':
        dispatch(applyAvailabilityToCategory({
          availabilityId: parseInt(availabilityApplyId, 10),
          categoryId: parseInt(id, 10),
        }));
        break;
      case 'item':
        dispatch(applyAvailabilityToItem({
          availabilityId: parseInt(availabilityApplyId, 10),
          itemId: parseInt(id, 10),
        }));
        break;
      case 'specialCategory':
        dispatch(applyAvailabilityToSpecialCategory({
          availabilityId: parseInt(availabilityApplyId, 10),
          specialCategoryId: parseInt(id, 10),
        }));
        break;
      case 'specialSize':
        dispatch(applyAvailabilityToSpecialSize({
          availabilityId: parseInt(availabilityApplyId, 10),
          specialSizeId: parseInt(id, 10),
        }));
        break;
      default:
    }
  };

  const handleSearchResult = filteredAvailabilityIds => {
    setAvailabilityList(memoizedAvailabilities.filter(availability => (
      filteredAvailabilityIds.includes(availability.id)
    )));
  };

  const availabilitiesForSearch = useMemo(
    () => memoizedAvailabilities.map(availability => ({
      id: availability.id,
      name: availability.name,
    })),
    [memoizedAvailabilities],
  );

  const handleAvailabilityDelete = () => {
    dispatch(deleteAvailability({
      id: parseInt(availabilityId, 10),
    }));
  };

  const handleAvailabilityEditSubmit = () => {
    if (isDataTheSame) {
      setIsAvailabilityModalOpen(false);
    } else {
      const availability = {};
      if ((availabilityName && !availabilityId)
        || (availabilityName && availabilityId && availabilityName !== availabilities[availabilityId]?.name)) {
        availability.name = availabilityName;
      }
      if ((selectedWeekDays.length && !availabilityId)
        || (selectedWeekDays.length && availabilityId
          && JSON.stringify(selectedWeekDays) !== JSON.stringify(availabilities[availabilityId]?.weekDays))) {
        availability.weekDays = selectedWeekDays;
      }
      if (startDate) {
        availability.startDate = startDate.toISOString();
      }
      if (endDate) {
        availability.endDate = endDate.toISOString();
      }
      if (!endDate && !!availabilities[availabilityId]?.endDate) {
        availability.endDate = null;
      }
      if (startTime) {
        availability.startTime = startTime.toISOString();
      }
      if (endTime) {
        availability.endTime = endTime.toISOString();
      }
      if (availabilityId) {
        availability.id = parseInt(availabilityId, 10);
      } else {
        availability[`${entity}Id`] = parseInt(id, 10);
      }

      if (availabilityId) {
        dispatch(editAvailability(availability));
      } else {
        switch (entity) {
          case 'category':
            dispatch(createAvailabilityForCategory(availability));
            break;
          case 'item':
            dispatch(createAvailabilityForItem(availability));
            break;
          case 'specialCategory':
            dispatch(createAvailabilityForSpecialCategory(availability));
            break;
          case 'specialSize':
            dispatch(createAvailabilityForSpecialSize(availability));
            break;
          default:
        }
      }
    }
  };

  const handleStartDateRemove = () => {
    setStartDate('');
  };

  const handleEndDateRemove = () => {
    setEndDate('');
  };

  const handleStartTimeRemove = () => {
    setStartTime('');
  };

  const handleEndTimeRemove = () => {
    setEndTime('');
  };

  return (
    <View
      onStartDateRemove={handleStartDateRemove}
      onEndDateRemove={handleEndDateRemove}
      onStartTimeRemove={handleStartTimeRemove}
      onEndTimeRemove={handleEndTimeRemove}
      isConfirmButtonDisabled={isConfirmButtonDisabled}
      onAvailabilityDelete={handleAvailabilityDelete}
      onAvailabilityDeleteModalChangeState={handleAvailabilityDeleteModalChangeState}
      onAvailabilityEditSubmit={handleAvailabilityEditSubmit}
      isAvailabilityDeleteModalOpen={isAvailabilityDeleteModalOpen}
      isAvailabilityEditing={!!availabilityId}
      startTime={startTime.toString()}
      endTime={endTime.toString()}
      startDate={startDate.toString()}
      endDate={endDate.toString()}
      onStartTimeChange={handleStartTimeChange}
      onEndTimeChange={handleEndTimeChange}
      onStartDateChange={handleStartDateChange}
      onEndDateChange={handleEndDateChange}
      onDayClick={handleDayClick}
      selectedWeekDays={selectedWeekDays}
      onAvailabilityNameChange={handleAvailabilityNameChange}
      availabilityName={availabilityName}
      onAvailabilityAdd={handleAvailabilityAdd}
      onAvailabilityModalChangeState={handleAvailabilityModalChangeState}
      isAvailabilityModalOpen={isAvailabilityModalOpen}
      onAvailabilityEdit={handleAvailabilityEdit}
      error={error}
      availabilities={availabilityList}
      availabilitiesForSearch={availabilitiesForSearch}
      isLoading={applyAvailabilityToCategoryPhase === LOADING
        || applyAvailabilityToItemPhase === LOADING
        || deleteAvailabilityPhase === LOADING
        || createAvailabilityForCategoryPhase === LOADING
        || createAvailabilityForItemPhase === LOADING
        || editAvailabilityPhase === LOADING
        || createAvailabilityForSpecialCategoryPhase === LOADING
        || createAvailabilityForSpecialSizePhase === LOADING
        || applyAvailabilityToSpecialCategoryPhase === LOADING
        || applyAvailabilityToSpecialSizePhase === LOADING}
      onBackUpClick={onBackUpClick}
      onAvailabilityClick={handleAvailabilityApply}
      onToastClose={handleToastClose}
      onSearch={handleSearchResult}
    />
  );
};

export default AvailabilitiesList;
